import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LOT_ORIGIN_LABELS_IT,
  LOT_STATUS_LABELS_IT,
  type LotOrigin,
  type LotStatus,
} from '@produzionejb/types';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@produzionejb/ui';
import { useDeleteLot, useLots } from '../../lib/lots-api';
import { XlsxExportButton } from '../../components/XlsxExportButton';

const PAGE_SIZE = 50;

const STATUS_BADGE: Record<LotStatus, string> = {
  ACTIVE: 'rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700',
  BLOCKED: 'rounded bg-red-100 px-2 py-0.5 text-xs text-red-700',
  EXHAUSTED: 'rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-700',
  ARCHIVED: 'rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700',
};

export function LotListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState<LotStatus | ''>('');
  const [origin, setOrigin] = useState<LotOrigin | ''>('');
  const [page, setPage] = useState(1);

  function handleSearchChange(v: string) {
    setSearch(v);
    setPage(1);
    window.clearTimeout((handleSearchChange as unknown as { t: number }).t);
    (handleSearchChange as unknown as { t: number }).t = window.setTimeout(() => {
      setDebouncedSearch(v.trim());
    }, 300);
  }

  const { data, isLoading, error } = useLots({
    page,
    pageSize: PAGE_SIZE,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(status && { status }),
    ...(origin && { origin }),
  });

  const deleteMut = useDeleteLot();

  async function handleDelete(id: string, code: string) {
    if (!window.confirm(t('lots.list.confirmDelete', { code }))) return;
    try {
      await deleteMut.mutateAsync(id);
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">{t('lots.list.title')}</h2>
        <div className="flex items-center gap-2">
          <XlsxExportButton
            path={`/api/lots?format=xlsx${debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : ''}${status ? `&status=${status}` : ''}${origin ? `&origin=${origin}` : ''}`}
            fallbackFilename="lotti.xlsx"
          />
          <Button onClick={() => navigate('/lots/new')}>{t('lots.list.newButton')}</Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('products.list.filtersTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-4">
          <div>
            <label className="text-xs text-slate-500">{t('lots.list.searchLabel')}</label>
            <Input
              placeholder={t('lots.list.searchPlaceholder')}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">{t('lots.fields.status')}</label>
            <select
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as LotStatus | '');
                setPage(1);
              }}
            >
              <option value="">{t('common.all')}</option>
              {Object.entries(LOT_STATUS_LABELS_IT).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">{t('lots.fields.origin')}</label>
            <select
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={origin}
              onChange={(e) => {
                setOrigin(e.target.value as LotOrigin | '');
                setPage(1);
              }}
            >
              <option value="">{t('common.all')}</option>
              {Object.entries(LOT_ORIGIN_LABELS_IT).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <span className="text-sm text-slate-600">
              {data ? t('lots.list.totalCount', { count: data.total }) : ''}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading && <p className="p-4 text-sm text-slate-500">{t('common.loading')}</p>}
          {error && (
            <p className="p-4 text-sm text-red-600">
              {t('lots.list.errorLoad')}: {(error as Error).message}
            </p>
          )}
          {data && data.items.length === 0 && (
            <p className="p-4 text-sm text-slate-500">{t('lots.list.empty')}</p>
          )}
          {data && data.items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-4 py-2">{t('lots.fields.code')}</th>
                  <th className="px-4 py-2">{t('lots.fields.origin')}</th>
                  <th className="px-4 py-2">{t('lots.fields.product')}</th>
                  <th className="px-4 py-2 text-right">{t('lots.fields.residualQty')}</th>
                  <th className="px-4 py-2">{t('lots.fields.parent')}</th>
                  <th className="px-4 py-2">{t('lots.fields.status')}</th>
                  <th className="px-4 py-2 text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((l) => {
                  const positionsTotal = l.positions.reduce((s, p) => s + p.residualQty, 0);
                  const showPositions = l.positions.length > 0;
                  const isUncategorized = !showPositions && l.productCode === 'DA_CATEGORIZZARE';
                  return (
                  <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50 align-top">
                    <td className="px-4 py-2 font-mono text-xs">{l.code}</td>
                    <td className="px-4 py-2 text-slate-700">{LOT_ORIGIN_LABELS_IT[l.origin]}</td>
                    <td className="px-4 py-2">
                      {showPositions ? (
                        <div className="space-y-0.5">
                          {l.positions.map((p) => (
                            <div key={p.productId} className="flex items-baseline justify-between gap-3">
                              <span>
                                <span className="font-medium text-slate-900">{p.productName}</span>
                                <span className="ml-1.5 font-mono text-xs text-slate-500">{p.productCode}</span>
                              </span>
                              <span className="font-mono text-xs text-slate-600">
                                {p.residualQty.toFixed(2)} {l.productUnit}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div>
                          <div className="font-medium text-slate-900">
                            {l.productName}
                            {isUncategorized && (
                              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                                {t('lots.list.uncategorizedBadge')}
                              </span>
                            )}
                          </div>
                          <div className="font-mono text-xs text-slate-500">{l.productCode}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-slate-700">
                      {(showPositions ? positionsTotal : l.residualQty).toFixed(2)} {l.productUnit}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-500">
                      {l.parentLotCode ?? '—'}
                    </td>
                    <td className="px-4 py-2">
                      <span className={STATUS_BADGE[l.status]}>
                        {LOT_STATUS_LABELS_IT[l.status]}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Link to={`/lots/${l.id}/edit`} className="mr-3 text-brand-700 hover:underline">
                        {t('common.edit')}
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleDelete(l.id, l.code)}
                        className="text-red-600 hover:underline"
                        disabled={deleteMut.isPending}
                      >
                        {t('common.delete')}
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {data && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ‹
          </Button>
          <span className="text-sm text-slate-600">
            {t('products.list.pageOf', { page, totalPages })}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            ›
          </Button>
        </div>
      )}
    </div>
  );
}
